// packages block
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, FormHelperText, IconButton, TextField } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { FC, MouseEvent, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { CustomControlProps } from "../interfaceTypes";
import { InfoTooltip } from "./InfoTooltip";
// styles, constants, utils and interfaces block

/**
 * CustomController is a custom form controller component that integrates with React Hook Form.
 *
 * @component
 * @prop {string} placeholder - The placeholder text for the input field.
 * @prop {string} helperText - The helper text to display below the input field.
 * @prop {string} controllerName - The name used to register the input field with React Hook Form.
 * @prop {string} controllerLabel - The label to display above the input field.
 * @prop {string} fieldType - The type of the input field (e.g., 'text', 'password', etc.).
 * @prop {boolean} isDisabled - Indicates whether the input field is disabled.
 * @prop {boolean} isMultiLine - Indicates whether the input field should be a multiline textarea.
 * @prop {number} maxLength - The maximum length allowed for the input value.
 * @prop {number} rowsLength - The number of rows for a multiline textarea.
 * @prop {boolean} readOnly - Indicates whether the input field is read-only.
 * @prop {string} defaultValue - The default value to populate the input field.
 * @prop {boolean} isShrink - Indicates whether the label should shrink when the input has value.
 * @prop {boolean} upperCase - Indicates whether the input value should be converted to uppercase.
 * @prop {boolean} dollarAdornment - Indicates whether the input should have '$' adornment.
 * @prop {boolean} percentageAdornment - Indicates whether the input should have '%' adornment.
 * @prop {boolean} monthAdornment - Indicates whether the input should have 'month' adornment.
 * @returns {JSX.Element} A custom form controller element with integrated React Hook Form.
 */
export const CustomController: FC<CustomControlProps> = ({
  placeholder,
  helperText,
  controllerName,
  controllerLabel,
  fieldType,
  isDisabled,
  isMultiLine,
  maxLength,
  rowsLength,
  readOnly,
  defaultValue,
  isShrink,
  upperCase,
  tooltipText,
  percentageAdornment,
  dollarAdornment,
  monthAdornment,
  customEndAdornment,
}): JSX.Element => {
  const { control } = useFormContext();
  const [showPassword, setShowPassword] = useState(fieldType === "text");

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Controller
      name={controllerName}
      control={control}
      render={({ field, fieldState: { invalid, error: { message } = {} } }) => (
        <Box>
          <TextField
            placeholder={placeholder}
            label={controllerLabel}
            type={showPassword ? "text" : fieldType}
            InputProps={{
              endAdornment: customEndAdornment ?? (tooltipText ? (
                <InputAdornment position="end">
                  <InfoTooltip title={tooltipText} />
                </InputAdornment>
              ) : fieldType === "password" ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOff sx={{ color: "gray" }} />
                    ) : (
                      <Visibility sx={{ color: "gray" }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ) : percentageAdornment ? (
                <InputAdornment position="start">%</InputAdornment>
              ) : monthAdornment ? (
                <InputAdornment position="start">Month(s)</InputAdornment>
              ) : undefined),
              startAdornment: dollarAdornment ? (
                <InputAdornment position="start">$</InputAdornment>
              ) : undefined,
            }}
            margin="dense"
            error={invalid}
            defaultValue={defaultValue}
            disabled={isDisabled}
            rows={isMultiLine ? rowsLength : undefined}
            fullWidth
            {...field}
            helperText={message}
            multiline={isMultiLine}
            sx={{
              ...(fieldType === "password" && {
                "& input::-ms-reveal": {
                  display: "none",
                },
                "& input::-ms-clear": {
                  display: "none",
                },
              }),
              ...(isMultiLine && {
                '& .MuiInputBase-root': {
                  height: 'auto',
                  display: 'flex',
                  alignItems: 'flex-start', // Keeps label/text at top
                  padding: '12px 14px',    // Standard MUI multiline padding
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    minHeight: 'unset',    // Removes any fixed height restrictions
                  },
                },
                '& .MuiInputBase-inputMultiline': {
                  padding: '0px',          // Prevents double padding inside textarea
                },
              }),
            }}
            variant="outlined"
            InputLabelProps={{ shrink: isShrink ? true : undefined }}
            inputProps={{
              style: {
                textTransform: upperCase ? "uppercase" : "inherit",
              },
              maxLength: maxLength || undefined,
              readOnly: readOnly ? true : false,
            }}
          />
          {!message && (
            <FormHelperText
              id="my-helper-text"
              sx={{ marginLeft: "0px !important" }}
            >
              {helperText}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  );
};
